<template>
    <div class="automation-action-settings">
        <div class="field">
            <label
                >{{ t('automations-form-actions-ai_content-model_label') }}</label
            >
            <select v-model="aiModel" class="form-control">
                <option value="basic">{{ t('automations-form-actions-ai_content-models-basic') }}</option>
                <option value="advanced">{{ t('automations-form-actions-ai_content-models-advanced') }}</option>
            </select>
            <div class="mt-2 model-description">
                <template v-if="aiModel === 'basic'">
                    <div>
                        <div class="badge badge-primary" v-html="t('automations-form-actions-ai_content-credits_label', [0.2])"></div>
                    </div>
                    <span v-html="t('automations-form-actions-ai_content-basic_description')"></span> <br />
                </template>
                <template v-else>
                    <div>
                        <div class="badge badge-primary" v-html="t('automations-form-actions-ai_content-credits_label', [1])"></div>
                    </div>
                    <span v-html="t('automations-form-actions-ai_content-advanced_description')"></span> <br />
                </template>
            </div>
            <div class="field mt-4">
                <label>
                    {{ t('automations-form-actions-ai_content-prompt_label') }} <br />
                    <small
                        >{{ t('automations-form-actions-ai_content-prompt_help') }}</small
                    >
                </label>
                <textarea
                    v-model="instruction"
                    class="form-control"
                    :placeholder="t('automations-form-actions-ai_content-placeholder')"
                    rows="10"
                ></textarea>
                <small>
                    {{ t('automations-form-actions-ai_content-snippets_help') }}
                </small>
                <small>
                    <ul>
                        <li class="token" v-for="(token, t) in tokens" :key="t">
                            <span class="copy-token">{{ token }}</span>
                            <a @click="copyToken(token)" class="copy-btn"
                                ><i class="fa fa-copy"></i
                            ></a>
                        </li>
                    </ul>
                </small>
                <small>
                    {{ t('automations-form-actions-ai_content-footer_help') }}
                </small>
            </div>

            <div v-if="aiModel === 'advanced'" class="field mt-4">
                <div class="advanced-toggle" @click="advancedMode = !advancedMode">
                    <i :class="advancedMode ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                    <strong>{{ t('automations-form-actions-ai_content-advanced_settings_label') }}</strong>
                </div>

                <div v-if="advancedMode" class="advanced-section mt-3">
                    <label>
                        {{ t('automations-form-actions-ai_content-output_values_label') }}
                        <br />
                        <small>
                            {{ t('automations-form-actions-ai_content-output_values_help') }}
                        </small>
                    </label>

                    <div
                        v-for="(field, index) in outputFields"
                        :key="index"
                        class="output-field-row mt-2"
                    >
                        <div class="output-field-inputs">
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model="field.key"
                                :placeholder="t('automations-form-actions-ai_content-field_id_placeholder')"
                                @input="sanitizeFieldKey(index)"
                            />
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model="field.description"
                                :placeholder="t('automations-form-actions-ai_content-field_instruction_placeholder')"
                            />
                            <button
                                class="btn btn-sm btn-outline-danger"
                                @click.prevent="removeOutputField(index)"
                                :title="t('automations-form-actions-ai_content-remove_field_title')"
                            >
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </div>

                    <button
                        class="btn btn-sm btn-outline-primary mt-2"
                        @click.prevent="addOutputField"
                    >
                        <i class="fa fa-plus"></i> {{ t('automations-form-actions-ai_content-add_field_button') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.model-description {
    font-size: 14px;
    color: #444;
    strong {
        font-weight: 500;
    }
}
.field label small {
    margin-top: 5px !important;
}
.advanced-toggle {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #555;
    &:hover {
        color: #333;
    }
    i {
        font-size: 11px;
        width: 12px;
    }
}
.advanced-section {
    border-left: 3px solid #e0e0e0;
    padding-left: 16px;
}
.output-field-row {
    .output-field-inputs {
        display: flex;
        gap: 8px;
        align-items: center;
        input:first-child {
            max-width: 200px;
        }
    }
}
</style>
<script>
export default {
    props: {
        properties: Object,
        conditions: Array,
        actionId: String,
    },
    data: function () {
        const existingOutputFields = this.properties.outputFields;
        return {
            title: this.t("automations-form-actions-ai_content-title"),
            description: this.t("automations-form-actions-ai_content-description"),
            aiModel: this.properties.aiModel
                ? this.properties.aiModel
                : "basic",
            instruction: this.properties.instruction
                ? this.properties.instruction
                : "",
            advancedMode: Array.isArray(existingOutputFields) && existingOutputFields.length > 0,
            outputFields: Array.isArray(existingOutputFields)
                ? existingOutputFields.map(f => ({ ...f }))
                : [],
        };
    },
    computed: {
        tokens: function () {
            return this.$store.getters["automationForm/tokens"](this.actionId);
        },
    },
    mounted: function () {
        this.$emit("mounted", this);
    },

    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        changed: function () {
            const props = {
                instruction: this.instruction,
                aiModel: this.aiModel,
            };
            if (this.advancedMode && this.outputFields.length > 0) {
                props.outputFields = this.outputFields.filter(f => f.key && f.description);
            }
            this.$emit("propertiesUpdated", props);
        },

        isValid: function () {
            return this.instruction !== "";
        },

        addOutputField: function () {
            this.outputFields.push({ key: "", description: "" });
        },

        removeOutputField: function (index) {
            this.outputFields.splice(index, 1);
            this.changed();
        },

        sanitizeFieldKey: function (index) {
            let key = this.outputFields[index].key;
            key = key.toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '');
            this.outputFields[index].key = key;
        },
    },
    watch: {
        aiModel: function (val) {
            if (val !== 'advanced') {
                this.advancedMode = false;
            }
            this.changed();
        },
        instruction: function () {
            this.changed();
        },
        outputFields: {
            handler: function () {
                this.changed();
            },
            deep: true,
        },
        advancedMode: function () {
            this.changed();
        },
    },
};
</script>
